import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { OrderService } from 'src/app/services/pedidos/pedidos.service';
import { rejectReasonsToFlatList } from 'src/app/services/pedidos/gateway-pedidos.mapper';
import { CacheWarmupService } from 'src/app/services/offline/cache-warmup.service';
import { SyncService } from 'src/app/services/offline/sync.service';
import { RejectReason } from 'src/app/shared/models/RejectReason';
import { ReturnReason } from 'src/app/shared/models/returnReason';
import { Order } from 'src/app/shared/models/orders';
import {
  AridosHistoryFacadeContract,
  AridosHistoryRow,
  AridosHistorySummary,
} from './aridos-history.types';

@Injectable({ providedIn: 'root' })
export class AridosHistoryFacade implements AridosHistoryFacadeContract {
  readonly ordersUpdated$ = this.cacheWarmup.ordersUpdated$.asObservable();

  constructor(
    private readonly orderService: OrderService,
    private readonly cacheWarmup: CacheWarmupService,
    private readonly syncService: SyncService,
  ) {}

  loadOrders(forceNetwork = false): Observable<AridosHistoryRow[]> {
    return from(this.orderService.getAllOrders(forceNetwork)).pipe(
      switchMap((orders$) => orders$),
      map((orders) => (orders || []).map((order) => this.mapOrder(order))),
    );
  }

  loadSummary(
    fromDate: string,
    toDate: string,
    forceNetwork = false,
  ): Observable<AridosHistorySummary> {
    return this.orderService.getResumenDespacho(fromDate, toDate, forceNetwork).pipe(
      map((rows: any[]) => {
        const summaryRows = (rows || []).map((row) => ({
          ticket: String(row?.TKT_H ?? ''),
          workName: String(row?.NOMBRE_PROYECTO_H ?? ''),
          deliveryAddress: String(row?.DIRECCION_ENTREGA_H ?? ''),
          week: String(row?.NUMEROSEMANAISO ?? ''),
          signed: Boolean(row?.FIRMADA),
          rejected: Boolean(row?.RECHAZADO),
        }));
        const signed = summaryRows.filter((row) => row.signed && !row.rejected).length;
        const rejected = summaryRows.filter((row) => row.rejected).length;
        return {
          rows: summaryRows,
          signed,
          rejected,
          unsigned: summaryRows.length - signed - rejected,
          total: summaryRows.length,
        };
      }),
    );
  }

  getRejectReasons(): Observable<RejectReason[]> {
    return this.orderService.getRejectReasons().pipe(
      map((data) => rejectReasonsToFlatList(data)),
    );
  }

  getReturnReasons(): Observable<ReturnReason[]> {
    return this.orderService.getReturnReasons().pipe(
      map((data) => data as ReturnReason[]),
    );
  }

  regenerateQr(ticket: string, orderNumber: string): Observable<unknown> {
    return this.orderService.setRegenerateQr(ticket, orderNumber);
  }

  setPendingOrder(ticket: string, orderNumber: string): Observable<unknown> {
    return this.orderService.setPendingOrder(ticket, orderNumber);
  }

  refresh(): void {
    this.syncService.flushNow();
  }

  getExportData(rows: AridosHistoryRow[]): unknown[] {
    return rows.map((row) => {
      const {
        businessVariant,
        locationLabel,
        showInstructions,
        showConcreteFields,
        showReturn,
        returnLabel,
        reasonLabel,
        showPallet,
        showSeal,
        sealLabel,
        orderLabel,
        showDocument,
        ...exportRow
      } = row;
      return exportRow;
    });
  }

  private mapOrder(order: Order): AridosHistoryRow {
    const { neg, ...normalizedOrder } = order;
    const variant = this.resolveVariant(neg);
    return {
      ...normalizedOrder,
      businessVariant: variant,
      locationLabel: variant === 'sacks' ? 'Destinatario:' : 'Obra:',
      showInstructions: variant === 'sacks' || variant === 'transport',
      showConcreteFields: false,
      showReturn: variant !== 'transport',
      returnLabel: variant === 'sacks' ? 'Devolución de sacos' : 'Retorno hormigón:',
      reasonLabel: variant === 'sacks' ? 'Motivo de devolución:' : 'Motivo de retorno:',
      showPallet: variant === 'sacks',
      showSeal: variant !== 'sacks',
      sealLabel: variant === 'transport' ? 'Numero de transporte:' : 'N° de sello:',
      orderLabel:
        variant === 'transport'
          ? 'Entrega:'
          : variant === 'sacks'
            ? 'N° de entrega:'
            : 'N° de pedido:',
      showDocument: false,
    };
  }

  private resolveVariant(neg: string): AridosHistoryRow['businessVariant'] {
    if (String(neg) === '3100') return 'sacks';
    if (String(neg) === '3400') return 'transport';
    return 'concrete';
  }
}
